import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import {
    nameSearchTagsEqual,
    normalizeNameSearchTag,
} from '#app/domain/funnel/activesRatings/normalizeNameSearchTag';
import type { ActivesRatingsDataset } from '#app/domain/funnel/activesRatings/types';

export type FunnelActivesRatingsSliceState = {
    dataset: ActivesRatingsDataset | null;
    loading: boolean;
    error: string | null;
    cacheKey: string | null;
    nameSearchTags: string[];
    selectedRatings: number[];
    selectedUserIds: string[];
};

const initialState: FunnelActivesRatingsSliceState = {
    dataset: null,
    loading: false,
    error: null,
    cacheKey: null,
    nameSearchTags: [],
    selectedRatings: [],
    selectedUserIds: [],
};

const funnelActivesRatingsSlice = createSlice({
    name: 'funnelActivesRatings',
    initialState,
    reducers: {
        setActivesRatingsLoading(state, action: PayloadAction<boolean>) {
            state.loading = action.payload;
        },
        setActivesRatingsDataset(
            state,
            action: PayloadAction<{ dataset: ActivesRatingsDataset; cacheKey: string }>
        ) {
            state.dataset = action.payload.dataset;
            state.cacheKey = action.payload.cacheKey;
            state.loading = false;
            state.error = null;
        },
        setActivesRatingsError(state, action: PayloadAction<string>) {
            state.error = action.payload;
            state.loading = false;
        },
        clearActivesRatingsError(state) {
            state.error = null;
        },
        clearActivesRatings(state) {
            state.dataset = null;
            state.cacheKey = null;
            state.loading = false;
            state.error = null;
        },
        addNameSearchTag(state, action: PayloadAction<string>) {
            const tag = normalizeNameSearchTag(action.payload);
            if (!tag) {
                return;
            }
            const exists = state.nameSearchTags.some((existing) =>
                nameSearchTagsEqual(existing, tag)
            );
            if (!exists) {
                state.nameSearchTags.push(tag);
            }
        },
        removeNameSearchTag(state, action: PayloadAction<string>) {
            state.nameSearchTags = state.nameSearchTags.filter(
                (tag) => !nameSearchTagsEqual(tag, action.payload)
            );
        },
        clearNameSearchTags(state) {
            state.nameSearchTags = [];
        },
        setActivesRatingsSelection(
            state,
            action: PayloadAction<{ selectedRatings: number[]; selectedUserIds: string[] }>
        ) {
            state.selectedRatings = action.payload.selectedRatings;
            state.selectedUserIds = action.payload.selectedUserIds;
        },
        clearActivesRatingsSelection(state) {
            state.selectedRatings = [];
            state.selectedUserIds = [];
        },
    },
});

export const {
    setActivesRatingsLoading,
    setActivesRatingsDataset,
    setActivesRatingsError,
    clearActivesRatingsError,
    clearActivesRatings,
    addNameSearchTag,
    removeNameSearchTag,
    clearNameSearchTags,
    setActivesRatingsSelection,
    clearActivesRatingsSelection,
} = funnelActivesRatingsSlice.actions;

export const funnelActivesRatingsSliceReducer = funnelActivesRatingsSlice.reducer;

export const selectFunnelActivesRatingsDataset = (state: {
    funnelActivesRatings?: FunnelActivesRatingsSliceState;
}) => state.funnelActivesRatings?.dataset ?? null;

export const selectFunnelActivesRatingsLoading = (state: {
    funnelActivesRatings?: FunnelActivesRatingsSliceState;
}) => Boolean(state.funnelActivesRatings?.loading);

export const selectFunnelActivesRatingsError = (state: {
    funnelActivesRatings?: FunnelActivesRatingsSliceState;
}) => state.funnelActivesRatings?.error ?? null;

export const selectFunnelActivesRatingsCacheKey = (state: {
    funnelActivesRatings?: FunnelActivesRatingsSliceState;
}) => state.funnelActivesRatings?.cacheKey ?? null;

export const selectActivesRatingsNameSearchTags = (state: {
    funnelActivesRatings?: FunnelActivesRatingsSliceState;
}) => state.funnelActivesRatings?.nameSearchTags ?? [];

export const selectActivesRatingsSelectedRatings = (state: {
    funnelActivesRatings?: FunnelActivesRatingsSliceState;
}) => state.funnelActivesRatings?.selectedRatings ?? [];

export const selectActivesRatingsSelectedUserIds = (state: {
    funnelActivesRatings?: FunnelActivesRatingsSliceState;
}) => state.funnelActivesRatings?.selectedUserIds ?? [];
